'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  LayoutDashboard,
  Package,
  Receipt,
  ShieldCheck,
  FileText,
  User,
  type LucideIcon,
} from 'lucide-react'
import {
  ACCOUNT_NAV_ITEMS,
  isAccountNavItemActive,
  type AccountNavItem,
} from './accountNavItems'

/**
 * Customer-portal sidebar (desktop) / bottom-tab nav (mobile).
 *
 * Brief §2 specifies a 6-item nav: Dashboard, Products, Orders, Warranty,
 * Documents, Profile. The nav model and the active-item resolver live in
 * accountNavItems.ts (pure, unit-tested); this file is a thin React shell.
 *
 * Layout:
 *   • md and up — vertical rail on the left, icon + label.
 *   • below md — horizontal bottom tab bar, icon-above-label.
 *
 * Visual: brand-token driven. Active state uses the tenant's CTA color via
 * `var(--color-cta)`. Touch targets ≥ 44×44 per brief §13 a11y contract.
 *
 * The component is purely presentational — no data fetching, no realtime.
 * If a future Tier 1 phase needs to surface counts (e.g. "Warranty (2)"),
 * that goes through a separate badge component fed by TanStack Query.
 */

const ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  Package,
  Receipt,
  ShieldCheck,
  FileText,
  User,
}

function renderIcon(name: string): React.ReactNode {
  const Icon = ICONS[name]
  if (!Icon) return null
  return <Icon size={20} aria-hidden="true" />
}

export default function AccountNav() {
  const pathname = usePathname() ?? '/account'

  return (
    <nav
      aria-label="Account navigation"
      className="
        md:flex md:flex-col md:gap-1 md:p-3 md:w-56 md:min-h-[calc(100vh-12rem)]
        md:border-r md:border-[var(--color-border)] md:bg-[var(--color-bg-primary)]
        fixed bottom-0 left-0 right-0 z-30 grid grid-cols-6 gap-0
        border-t border-[var(--color-border)] bg-[var(--color-bg-primary)]
        md:static md:border-t-0
      "
    >
      {ACCOUNT_NAV_ITEMS.map((item) => (
        <AccountNavLink
          key={item.href}
          item={item}
          active={isAccountNavItemActive(pathname, item.href)}
        />
      ))}
    </nav>
  )
}

function AccountNavLink({
  item,
  active,
}: {
  item: AccountNavItem
  active: boolean
}) {
  // Mobile: icon over label, vertical stack. Desktop: icon left of
  // label, horizontal. Both layouts respect the 44×44 minimum touch
  // target via min-h-[44px] + py.
  const base =
    'group flex items-center justify-center md:justify-start gap-2 ' +
    'min-h-[44px] min-w-[44px] px-2 py-2 md:px-3 md:py-2.5 ' +
    'text-xs md:text-sm font-medium rounded-md ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cta-edge)] focus-visible:ring-offset-1 ' +
    'flex-col md:flex-row'
  const activeCls =
    'text-[var(--color-cta-readable)] md:bg-[color-mix(in_srgb,var(--color-cta)_10%,transparent)]'
  const inactiveCls =
    'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] md:hover:bg-[var(--color-bg-secondary)]'

  return (
    <Link
      href={item.href}
      aria-current={active ? 'page' : undefined}
      className={`${base} ${active ? activeCls : inactiveCls}`}
    >
      {renderIcon(item.icon)}
      <span className="leading-none md:leading-normal">{item.label}</span>
    </Link>
  )
}
